import { DEMO_FIELD_TYPE, DEMO_CALCULATOR_STEP } from 'config/constants'
import { MutableRefObject } from 'react'

import {
  DEMO_INVOICE_DATE_CHOOSE_EVENT,
  DEMO_INVOICE_DATE_OPEN_EVENT,
  DEMO_INVOICE_LOCATION_CHOOSE_EVENT,
  DEMO_INVOICE_LOCATION_SEARCH_EVENT,
  DEMO_INVOICE_READY_EVENT,
  DEMO_INVOICE_SAVE_EVENT,
  DEMO_INVOICE_STAGE_SELECTORS,
  DEMO_INVOICE_STATIC_VALUE_SELECTOR,
  DEMO_STEP_DELAY,
} from '../../playback-constants'
import {
  animateAction,
  prepareFields,
  restoreFields,
  restoreStaticValues,
  wait,
  waitForDocumentElement,
  waitForStepData,
} from '../../helpers'
import { playDemoFields } from '../../play-fields'

export type PackagingFormState = {
  fields: AnimatedField[]
  remainingFields: AnimatedField[]
  saveProductAction: HTMLElement | null
}

type InvoiceStepParams = {
  activeInvoiceValuesRef: MutableRefObject<StaticValueState[]>
  controls: PlaybackControls
  fields: AnimatedField[]
  stepElement: HTMLElement
}

const showStaticValues = (values: StaticValueState[]) => {
  values.forEach(({ element }) => {
    element.style.transition = 'opacity 180ms ease'
    element.style.opacity = '1'
  })
}

export const playInvoiceStep = async ({
  activeInvoiceValuesRef,
  controls,
  fields: initialFields,
  stepElement,
}: InvoiceStepParams): Promise<PackagingFormState | null> => {
  let fields = initialFields
  let remainingFields = fields.filter(field => !field.element.closest('.invoice-form'))
  let saveProductAction = stepElement.querySelector<HTMLElement>(
    '[data-demo-playback-action="save-product"]',
  )

  const invoiceFields = fields.filter(field => field.element.closest('.invoice-form'))
  const saveInvoiceAction = stepElement.querySelector<HTMLElement>(
    '[data-demo-playback-action="save-invoice"]',
  )
  const invoiceStages = DEMO_INVOICE_STAGE_SELECTORS
    .map(selector => stepElement.querySelector<HTMLElement>(selector))
    .filter((element): element is HTMLElement => Boolean(element))
  const invoiceStageStates = invoiceStages.map(stageElement => ({
    stageElement,
    valueElements: Array.from(
      stageElement.querySelectorAll<HTMLElement>(DEMO_INVOICE_STATIC_VALUE_SELECTOR),
    ).map(element => ({
      element,
      opacity: element.style.opacity,
      transition: element.style.transition,
    })),
  }))
  const invoiceValueStates = invoiceStageStates.flatMap(stage => stage.valueElements)

  invoiceValueStates.forEach(({ element }) => {
    element.style.opacity = '0'
  })
  activeInvoiceValuesRef.current = invoiceValueStates

  let hasTypedInvoiceFields = true

  for (const stage of invoiceStageStates) {
    const isDateStage = stage.stageElement.classList.contains('invoice-form__invoice_date')
    const isIncotermsStage = stage.stageElement.classList.contains('invoice-form__incoterms')
    const isLocationStage = stage.stageElement.classList.contains('invoice-form__location')

    if (isDateStage) {
      const dateIsChosen = await playDemoFields({
        controls,
        fields: [
          {
            element: stage.stageElement,
            name: 'invoice_date',
            onChoose: () => controls.dispatchEvent(new CustomEvent(DEMO_INVOICE_DATE_CHOOSE_EVENT)),
            onOpen: () => controls.dispatchEvent(new CustomEvent(DEMO_INVOICE_DATE_OPEN_EVENT)),
            type: DEMO_FIELD_TYPE.DATE,
            value: '',
          },
        ],
        step: DEMO_CALCULATOR_STEP.PACKAGING,
        visibleFields: fields,
      })

      if (!dateIsChosen) {
        hasTypedInvoiceFields = false
        break
      }

      showStaticValues(stage.valueElements)
      continue
    }

    if (isIncotermsStage) {
      const select = stage.stageElement.querySelector<HTMLElement>('[role="combobox"]')
      const value = select?.textContent?.trim() || ''

      if (!select || !value) {
        hasTypedInvoiceFields = false
        break
      }

      const incotermsIsChosen = await playDemoFields({
        controls,
        fields: [
          {
            element: select,
            list: [ { label: value, value } ],
            name: 'incoterms',
            type: DEMO_FIELD_TYPE.SELECT,
            value,
          },
        ],
        step: DEMO_CALCULATOR_STEP.PACKAGING,
        visibleFields: fields,
      })

      if (!incotermsIsChosen) {
        hasTypedInvoiceFields = false
        break
      }

      showStaticValues(stage.valueElements)
      continue
    }

    if (isLocationStage) {
      const query = stage.stageElement.dataset.demoSearchQuery || ''
      const input = stage.stageElement.querySelector<HTMLInputElement>('input')

      if (!input || !query) {
        hasTypedInvoiceFields = false
        break
      }

      const locationIsChosen = await playDemoFields({
        controls,
        fields: [
          {
            element: input,
            name: 'invoice_location',
            onChoose: () => controls.dispatchEvent(new CustomEvent(DEMO_INVOICE_LOCATION_CHOOSE_EVENT)),
            onType: value => controls.dispatchEvent(new CustomEvent(DEMO_INVOICE_LOCATION_SEARCH_EVENT, {
              detail: { query: value },
            })),
            optionSelector: '.MuiAutocomplete-popper .location-group__main, .MuiAutocomplete-popper .location-option',
            shouldScroll: false,
            type: DEMO_FIELD_TYPE.AUTOCOMPLETE,
            value: query,
          },
        ],
        step: DEMO_CALCULATOR_STEP.PACKAGING,
        visibleFields: fields,
      })

      if (!locationIsChosen) {
        hasTypedInvoiceFields = false
        break
      }

      const selectedLocationValue = await waitForDocumentElement(
        '[data-calculator-step="packaging"] .invoice-form__location .location-value',
        controls.isCancelled,
      )

      if (selectedLocationValue) {
        selectedLocationValue.style.transition = 'opacity 180ms ease'
        selectedLocationValue.style.opacity = '1'
      }

      await wait(DEMO_STEP_DELAY)
      continue
    }

    const stageFocusTarget = stage.stageElement.querySelector<HTMLElement>(
      '[role="combobox"], input, textarea, button',
    )

    stageFocusTarget?.focus({ preventScroll: true })
    showStaticValues(stage.valueElements)

    const stageFields = invoiceFields.filter(field => stage.stageElement.contains(field.element))
    hasTypedInvoiceFields = await controls.typeFields(stageFields)

    if (!hasTypedInvoiceFields) {
      break
    }

    if (!stageFields.length) {
      await wait(DEMO_STEP_DELAY)
    }

    stageFocusTarget?.blur()
  }

  if (!hasTypedInvoiceFields) {
    restoreStaticValues(invoiceValueStates)
    controls.clearFields(fields)
    activeInvoiceValuesRef.current = []
    return null
  }

  if (!saveInvoiceAction) {
    return { fields, remainingFields, saveProductAction }
  }

  await wait(DEMO_STEP_DELAY)
  controls.setActiveFieldElement(null)
  controls.dispatchEvent(new CustomEvent(DEMO_INVOICE_READY_EVENT))

  const map = await waitForDocumentElement(
    '[data-calculator-step="packaging"] .invoice-form__map .mapboxgl-canvas',
    controls.isCancelled,
  )

  if (!map) {
    restoreStaticValues(invoiceValueStates)
    activeInvoiceValuesRef.current = []
    return null
  }

  await animateAction(saveInvoiceAction)

  restoreFields(fields)
  controls.showFields([])
  controls.dispatchEvent(new CustomEvent(DEMO_INVOICE_SAVE_EVENT))
  await wait(DEMO_STEP_DELAY)

  const productContainer = await waitForDocumentElement(
    '[data-calculator-step="packaging"] .products',
    controls.isCancelled,
  )

  if (!productContainer) {
    restoreStaticValues(invoiceValueStates)
    activeInvoiceValuesRef.current = []
    return null
  }

  const hasProductData = await waitForStepData(productContainer, controls.isCancelled)

  if (!hasProductData) {
    restoreStaticValues(invoiceValueStates)
    activeInvoiceValuesRef.current = []
    return null
  }

  const productCodeField = await waitForDocumentElement(
    '[data-calculator-step="packaging"] .add-product__code input',
    controls.isCancelled,
  )

  if (!productCodeField) {
    restoreStaticValues(invoiceValueStates)
    activeInvoiceValuesRef.current = []
    return null
  }

  fields = prepareFields(productContainer)
  remainingFields = fields
  saveProductAction = productContainer.querySelector<HTMLElement>(
    '[data-demo-playback-action="save-product"]',
  )
  controls.showFields(fields)

  return { fields, remainingFields, saveProductAction }
}
